import { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { Menu } from 'lucide-react';
import { LuChevronRight, LuMessageCircle, LuPhone } from 'react-icons/lu';

import { cn } from '@mds/shared/lib/utils';
import { useScrolled } from '@mds/shared/hooks/use-scrolled';
import { Button } from '@mds/shared/ui/shadcn/button';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@mds/shared/ui/shadcn/sheet';

import { CONTACT, NAV, SITE } from '@/data';

/**
 * Pages that open with a full-bleed hero. On those the header starts as part of
 * the picture — no bar, no rule, light type — and becomes the ordinary solid
 * bar as soon as the page scrolls and something other than the hero is behind
 * it. Everywhere else it is solid from the first frame.
 */
const HERO_ROUTES = new Set<string>(['/']);

/**
 * A nav item's classes: two states crossed with two grounds.
 *
 * Resting links are the full-strength ink on the white bar and the full-strength
 * white on the hero; the current page is the brand mint on both — the
 * text-grade green on white, the hero's own bright step over the photograph.
 * The hero keeps its own tokens because it sits over a photograph behind a
 * scrim, not over the page.
 */
function navLink(isActive: boolean, onHero: boolean) {
  return cn(
    'rounded-button px-4 py-2 text-lg whitespace-nowrap transition-colors',
    'focus-visible:outline-2 focus-visible:outline-offset-2',
    onHero ? 'focus-visible:outline-focus-on-panel' : 'focus-visible:outline-focus',
    isActive ? 'font-semibold' : 'font-medium',
    onHero
      ? isActive
        ? 'text-(--hero-eyebrow-fg)'
        : 'text-(--hero-fg) hover:text-(--hero-eyebrow-fg)'
      : isActive
        ? 'text-brand-text'
        : 'text-ink hover:text-brand-text',
  );
}

export default function SiteHeader() {
  const { pathname } = useLocation();
  const scrolled = useScrolled();
  const [menuOpen, setMenuOpen] = useState(false);

  const onHero = HERO_ROUTES.has(pathname) && !scrolled;

  return (
    // Transparent over the hero, with light type on the photograph's scrim; the
    // white surface everywhere else, with the page's own ink. Only the hero
    // state is a `data-ground="panel"` subtree — the dark ground the scrim is —
    // so on the white bar `text-ink`, `text-brand-text` and `border-hairline`
    // keep their ordinary light-page meanings.
    //
    // `-mb-px` hands back the border's pixel, so the bar takes exactly
    // `h-header` of the flow. Without it the hero's `-mt-header` pull falls one
    // pixel short and the page's white shows as a line above the photograph.
    <header
      data-ground={onHero ? 'panel' : undefined}
      className={cn(
        'sticky top-0 z-(--z-header) -mb-px border-b transition-[background-color,border-color,color]',
        'duration-(--duration-fast) ease-standard',
        onHero
          ? 'border-transparent bg-transparent text-(--hero-fg)'
          : 'border-hairline bg-surface text-ink',
      )}
    >
      {/* Two columns on a phone — the burger, then the wordmark beside it.
          Three from `lg`, with the nav in the middle and an empty third; that
          empty column is what centres the row against the bar rather than
          against whatever the wordmark happens to be wide.

          The reordering is `order-*` rather than a second copy of the markup:
          grid auto-placement walks children in order-modified document order,
          so one burger renders in one place and lands in a different cell per
          breakpoint. Source order stays logo-first, which is what a screen
          reader and the tab key follow. */}
      {/* No gap on a phone: the burger is a 44px box around a 16px icon, so
          14px of its own space already sits between the lines and the logo;
          a gap on top of that read as the logo drifting away. */}
      <div className="mx-auto grid h-header max-w-page grid-cols-[auto_1fr] items-center gap-0 px-gutter lg:grid-cols-[1fr_auto_1fr] lg:gap-4">
        {/* The logo in the version made for its ground: colour-on-dark (white
            wordmark) over the hero's scrim, colour-on-light on the white bar.
            `justify-self-start` so the link is only as wide as the logo rather
            than its whole grid cell. */}
        <Link
          to="/"
          onClick={() => setMenuOpen(false)}
          className={cn(
            'order-2 justify-self-start focus-visible:outline-2 focus-visible:outline-offset-4 lg:order-1',
            onHero ? 'focus-visible:outline-focus-on-panel' : 'focus-visible:outline-focus',
          )}
        >
          <img
            src={
              onHero ? '/images/mds-logo-color-on-dark.svg' : '/images/mds-logo-color-on-light.svg'
            }
            alt={`${SITE.name}, ${SITE.fullName}`}
            // Smaller below `lg`, where the bar holds just the logo and the burger.
            className="h-9 w-auto lg:h-12"
          />
        </Link>

        <nav aria-label="Main" className="order-3 hidden justify-self-center lg:order-2 lg:block">
          <ul className="flex list-none items-center gap-2">
            {NAV.map((item) => (
              <li key={item.to}>
                <NavLink to={item.to} className={({ isActive }) => navLink(isActive, onHero)}>
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Open menu"
              // The ghost button's own hover is shadcn's light wash, drawn for a
              // white ground — right on the white bar. Over the hero it takes
              // the hero's translucent white wash instead.
              // `size-(--target-min)`: the 44px tap target Apple's guidelines
              // and the project ask of every phone control, up from the icon
              // size's 36px. `-ml-3` pulls the bigger box out by its extra
              // padding, so the three lines stay where they were against the
              // page edge.
              className={cn(
                'order-1 -ml-3 size-(--target-min) lg:hidden',
                onHero &&
                  'text-inherit hover:bg-(--hero-nav-hover-bg) hover:text-inherit focus-visible:border-focus-on-panel focus-visible:ring-focus-on-panel/50',
              )}
            >
              <Menu />
            </Button>
          </SheetTrigger>

          {/* The panel is always the light surface, whatever ground the bar was
              on when it opened — it covers the hero rather than sitting over
              it, so the dark pairings do not apply inside. Rounded on the
              open edge only; the other three sit flush with the screen. */}
          <SheetContent
            side="left"
            showCloseButton={false}
            className="w-3/4 max-w-xs gap-6 rounded-r-card border-r-0 p-4"
          >
            <SheetHeader className="flex-row items-center justify-between p-0">
              <SheetTitle asChild>
                <Link
                  to="/"
                  onClick={() => setMenuOpen(false)}
                  className="focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-focus"
                >
                  <img
                    src="/images/mds-logo-color-on-light.svg"
                    alt={`${SITE.name}, ${SITE.fullName}`}
                    className="h-9 w-auto"
                  />
                </Link>
              </SheetTitle>
            </SheetHeader>

            {/* Home leads the list here and nowhere else: on the bar the logo
                is the way home, and in the drawer it is easy to miss. */}
            <nav aria-label="Main">
              <ul className="flex list-none flex-col gap-1">
                {[{ label: 'Home', to: '/' }, ...NAV].map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.to === '/'}
                      onClick={() => setMenuOpen(false)}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center justify-between rounded-input px-4 py-3 text-lg font-medium transition-colors',
                          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                          isActive ? 'bg-brand-wash text-brand-text' : 'text-ink hover:bg-raised',
                        )
                      }
                    >
                      {item.label}
                      <LuChevronRight aria-hidden="true" className="size-4 opacity-70" />
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>

            {/* One number behind both buttons — calls and WhatsApp reach the
                same phone. */}
            <SheetFooter className="mt-auto grid grid-cols-2 gap-3 p-0">
              <Button asChild variant="outline" size="lg">
                <a href={CONTACT.phoneHref}>
                  <LuPhone />
                  Call
                </a>
              </Button>
              <Button asChild size="lg">
                <a href={CONTACT.whatsappHref} target="_blank" rel="noreferrer">
                  <LuMessageCircle />
                  WhatsApp
                </a>
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
